import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  SegmentedControl,
  SegmentedItem,
  useIsMobile,
} from "../../../components/ui";
import ClientMealPlansPanel from "./ClientMealPlansPanel";
import ClientPlanBanner from "./ClientPlanBanner";
// Deep imports rather than the feature barrels: these are the only things the
// tab needs out of either module, and both barrels carry their dialogs and
// pages behind them.
import ExercisePlansPanel from "../../exercisePlans/components/ExercisePlansPanel";
import ClientSupplementsPanel from "../../supplements/components/ClientSupplementsPanel";
import type { SubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
import { ClientDetail } from "../types/clients.types";

/** The three lists the tab switches between. */
type PlanSection = "meal" | "supplement" | "exercise";

const SECTIONS: SegmentedItem<PlanSection>[] = [
  { id: "meal", label: "Meal" },
  { id: "supplement", label: "Supplement" },
  { id: "exercise", label: "Exercise" },
];

/** An unknown or missing `?kind=` opens on Meal, rather than on nothing. */
const sectionFromParam = (value: string | null): PlanSection =>
  SECTIONS.some((section) => section.id === value)
    ? (value as PlanSection)
    : "meal";

export interface ClientPlansTabProps {
  client: ClientDetail;
  /**
   * Whether this workspace may still write plans, read once by the screen
   * above — see `ClientDetailPage`. Handed to whichever panel is on screen;
   * what each one does with it is its own.
   */
  gate: SubscriptionGate;
}

/**
 * A client's Plans tab: the plan they are paying for, and the three kinds of
 * plan written under it.
 *
 * The section lives in the URL as `?kind=`, beside the `?tab=` above it, and
 * for the same reason: the supplement builder is a screen of its own, and
 * closing it, filing it as a draft or sending it all return here. Held in
 * state alone it came back to the Meal list every time, whichever list it had
 * been opened from — the tab was remembered and the list under it was not.
 *
 * Replaced rather than pushed, like the tab: Back should leave the client
 * rather than walk back through every list that was looked at along the way.
 *
 * Only the section on screen is mounted. Each panel fetches or filters its own
 * list, and three of them mounted at once would do all that work for the two
 * lists nobody is looking at.
 *
 * On a phone this tab is the three lists and nothing else. Each panel draws
 * itself as a column of cards there — see `PlanSendCards` — with one thing to
 * do to a plan: send it to the client. Creating a plan and opening one both
 * lead into a builder the app does not carry at that width, so neither is
 * offered rather than being offered and refused.
 */
const ClientPlansTab: React.FC<ClientPlansTabProps> = ({ client, gate }) => {
  const [params, setParams] = useSearchParams();
  const section = sectionFromParam(params.get("kind"));

  const isMobile = useIsMobile();

  // `tab` is written back alongside it: this tab is only ever on screen under
  // `tab=plans`, and a `kind` on its own would be a URL that opens elsewhere.
  const setSection = (kind: PlanSection) =>
    setParams({ tab: "plans", kind }, { replace: true });

  return (
    <div className="flex flex-col gap-4 min-h-0 flex-1">
      {/* Current subscription — read off this client's payment requests, since
          a plan is what a paid one bought. It fetches its own. Above the
          switch, because it is true of all three lists under it.

          Not on a phone. It is a row of three labelled figures and a tag,
          which at that width wraps into four lines of small print before the
          thing the tab was opened for — and every one of those figures is on
          the Payments tab next door, which is where a subscription actually
          lives. */}
      {!isMobile && <ClientPlanBanner clientId={client.id} />}

      <SegmentedControl
        items={SECTIONS}
        value={section}
        onChange={setSection}
        label="Plan kind"
        // Across the screen on a phone, where the row is the full width
        // anyway and three hugged pills leave a third of it empty.
        fill={isMobile}
        className="flex-shrink-0"
      />

      {section === "meal" && <ClientMealPlansPanel client={client} gate={gate} />}

      {section === "supplement" && (
        <ClientSupplementsPanel
          clientId={client.id}
          clientName={client.name}
          gate={gate}
        />
      )}

      {section === "exercise" && (
        <ExercisePlansPanel
          clientId={client.id}
          clientName={client.name}
          gate={gate}
        />
      )}
    </div>
  );
};

export default ClientPlansTab;
